
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

import {
  Box,
  Button,
  IconButton,
  Typography,
} from '@mui/material';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import DOMPurify from 'dompurify';

import {
  BannerViewer,
  DynamicBanner,
  BannerType,
} from './bannerTypes';

import {
  hasBannerContent,
  getBannerRevision,
  isBannerScheduled,
  matchesBannerPath,
  matchesBannerTarget,
  safeBannerUrl,
  sortBannersByPriority,
} from './bannerUtils';

import { useDynamicBanners } from './useDynamicBanners';

// -------------------------------------------------------
// Types
// -------------------------------------------------------

interface DynamicBannerOverlayProps {
  viewer: BannerViewer | null;

  // Default banner location
  placement?: string;

  // Width occupied by the PepWise side navigation.
  // Set to 0 if the Header already handles the offset.
  sidebarOffset?: number;

  // Optional selector for the bottom-most header row.
  // When omitted, the component uses the DOM element
  // immediately preceding its insertion point.
  anchorSelector?: string;
}

type BannerStyleConfig = Record<BannerType, {
  background: string;
  text: string;
  accent: string;
  fontSize: number;
  buttonBackground: string;
  buttonHoverBackground: string;
  buttonBorder: string;
}>;

interface BannerItemProps {
  banner: DynamicBanner;
  bannerStyleConfig: BannerStyleConfig;
  onDismiss: (banner: DynamicBanner) => void;
  extraAction?: React.ReactNode;
  showDivider?: boolean;
}

interface OverlayPosition {
  top: number;
  left: number;
  width: number;
}

// -------------------------------------------------------
// Banner icons
// -------------------------------------------------------

const BANNER_ICONS: Record<
  BannerType,
  React.ReactElement
> = {
  info: <InfoOutlinedIcon fontSize="small" />,

  success: (
    <CheckCircleOutlineIcon fontSize="small" />
  ),

  warning: (
    <WarningAmberOutlinedIcon fontSize="small" />
  ),

  critical: (
    <ErrorOutlineOutlinedIcon fontSize="small" />
  ),
};

const getBannerColors = (banner: DynamicBanner, bannerStyleConfig: BannerStyleConfig) => {
  const defaults = bannerStyleConfig[banner.type] || bannerStyleConfig.info;
  const styling = banner.styling;
  const validColor = (value: unknown, fallback: string): string =>
    typeof value === 'string' && /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value)
      ? value
      : fallback;
  const parsedSize = typeof styling?.fontSize === 'string' &&
    /^\d+(?:\.\d+)?px$/.test(styling.fontSize.trim())
    ? Number.parseFloat(styling.fontSize)
    : Number.NaN;
  const borderColor = validColor(styling?.borderColor, defaults.accent);

  return {
    ...defaults,
    background: validColor(styling?.backgroundColor, defaults.background),
    text: validColor(styling?.fontColor, defaults.text),
    title: validColor(styling?.titleColor, defaults.text),
    accent: borderColor,
    fontSize: Number.isFinite(parsedSize)
      ? Math.max(12, Math.min(20, parsedSize))
      : defaults.fontSize,
    buttonBackground: validColor(styling?.borderColor, defaults.buttonBackground),
    buttonBorder: validColor(styling?.borderColor, defaults.buttonBorder),
  };
};

// -------------------------------------------------------
// Sanitize HTML content
// -------------------------------------------------------

const sanitizeBannerHtml = (
  html: string
): string => {
  if (!html) {
    return '';
  }

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'b',
      'strong',
      'i',
      'em',
      'u',
      's',
      'span',
      'a',
      'ul',
      'ol',
      'li',
      'code',
    ],

    ALLOWED_ATTR: [
      'href',
      'title',
    ],

    ALLOW_DATA_ATTR: false,
  });
};

// -------------------------------------------------------
// Individual banner UI
// -------------------------------------------------------

const BannerItem: React.FC<BannerItemProps> = ({
  banner,
  bannerStyleConfig,
  onDismiss,
  extraAction,
  showDivider = false,
}) => {
  const colors = getBannerColors(banner, bannerStyleConfig);
  const ctaColors = bannerStyleConfig[banner.type];

  const sanitizedHtml = useMemo(
    () => sanitizeBannerHtml(banner.bannerText),
    [banner.bannerText]
  );

  const ctaUrl = safeBannerUrl(banner.cta?.url);

  const bannerIcon =
    BANNER_ICONS[banner.type] ||
    BANNER_ICONS.info;

  return (
    <Box
      role="group"
      aria-label={
        banner.bannerTitle ||
        `${banner.type} announcement`
      }
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        px: 2,
        py: 1.25,
        backgroundColor: `${colors.background} !important`,
        color: `${colors.text} !important`,
        borderLeft: `4px solid ${colors.accent}`,
        borderTop: showDivider
          ? '1px solid rgba(0, 0, 0, 0.08)'
          : 'none',
      }}
    >
      {/* Banner icon */}

      <Box
        aria-hidden="true"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: colors.title,
        }}
      >
        {bannerIcon}
      </Box>

      {/* Banner content */}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minWidth: 0,
          gap: 0.5,
        }}
      >
        {banner.bannerTitle && (
          <Typography
            component="div"
            sx={{
              fontSize: Math.max(
                14,
                colors.fontSize + 1
              ),
              fontWeight: 500,
              lineHeight: 1.4,
              color: colors.title,
              overflowWrap: 'anywhere',
            }}
          >
            {banner.bannerTitle}
          </Typography>
        )}

        {sanitizedHtml && (
          <Box
            sx={{
              fontSize: `${colors.fontSize}px`,
              fontWeight: 400,
              lineHeight: 1.5,
              color: colors.text,
              overflowWrap: 'anywhere',

              '& p': {
                margin: 0,
              },

              '& p + p': {
                marginTop: '6px',
              },

              '& a': {
                color: 'inherit',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                fontWeight: 600,
              },

              '& a:focus-visible': {
                outline: '2px solid currentColor',
                outlineOffset: '2px',
              },

              '& ul, & ol': {
                marginTop: '4px',
                marginBottom: '4px',
                paddingLeft: '20px',
              },
            }}
            dangerouslySetInnerHTML={{
              __html: sanitizedHtml,
            }}
          />
        )}

        {/* Optional CTA */}

        {ctaUrl && banner.cta?.enabled !== false && banner.cta?.text && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: 0.5,
            }}
          >
            <Button
              component="a"
              href={ctaUrl}
              target={banner.cta?.target === '_self' ? '_self' : '_blank'}
              rel="noopener noreferrer"
              variant="outlined"
              size="small"
              sx={{
                minWidth: 109,
                minHeight: 22,
                height: 22,
                px: 1.25,
                py: 0,
                border: `1px solid ${ctaColors.buttonBorder} !important`,
                borderColor: `${ctaColors.buttonBorder} !important`,
                color: `${ctaColors.buttonBorder} !important`,
                fontSize: '12px',
                fontWeight: 500,
                textTransform: 'none',
                borderRadius: '4px',
                boxShadow: 'none',

                '&:hover, &:active': {
                  borderColor: `${ctaColors.buttonBorder} !important`,
                  color: `${ctaColors.buttonBorder} !important`,
                  backgroundColor: `${ctaColors.buttonHoverBackground} !important`,
                  boxShadow: 'none',
                },

                '&:focus-visible, &.Mui-focusVisible': {
                  outline: `2px solid ${ctaColors.buttonBorder}`,
                  outlineOffset: '2px',
                  borderColor: `${ctaColors.buttonBorder} !important`,
                  color: `${ctaColors.buttonBorder} !important`,
                  backgroundColor: `${ctaColors.buttonBackground} !important`,
                },
              }}
            >
              {banner.cta.text}
            </Button>
          </Box>
        )}
      </Box>

      {/* Right-side actions */}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          flexShrink: 0,
          ml: 0.5,
        }}
      >
        {extraAction}

        {banner.isDismissible && (
          <IconButton
            aria-label={
              `Dismiss ${banner.bannerTitle || 'announcement'}`
            }
            size="small"
            onClick={() => onDismiss(banner)}
            sx={{
              width: 28,
              height: 28,
              color: `${colors.text} !important`,
              borderRadius: '4px',

              '&:hover, &:active': {
                color: `${colors.text} !important`,
              },

              '&:focus-visible, &.Mui-focusVisible': {
                outline: '2px solid currentColor',
                outlineOffset: '2px',
                color: `${colors.text} !important`,
              },
              '& .MuiSvgIcon-root': {
                color: `${colors.text} !important`,
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

interface BannerEligibilityContext {
  viewer: BannerViewer;
  dashboardData: ReturnType<typeof useDynamicBanners>['dashboardData'];
  dismissals: ReturnType<typeof useDynamicBanners>['dismissals'];
  now: number;
  pathname: string;
  placement: string;
}

const matchesBannerPage = (
  banner: DynamicBanner,
  dashboardData: BannerEligibilityContext['dashboardData'],
  pathname: string
): boolean => {
  const dashboardUrl = banner.dashboardUrl || dashboardData?.dashboardUrl;

  return (
    banner.isGlobal === true ||
    dashboardData?.isGlobal === true ||
    Boolean(dashboardUrl && matchesBannerPath(dashboardUrl, pathname))
  );
};

const isBannerEligible = (
  banner: DynamicBanner,
  { viewer, dashboardData, dismissals, now, pathname, placement }: BannerEligibilityContext
): boolean => {
  if (
    !banner?.bannerId ||
    !banner.isActive ||
    !hasBannerContent(banner)
  ) {
    return false;
  }

  // Placement targeting

  if (
    banner.placement &&
    banner.placement !== placement
  ) {
    return false;
  }

  // Page targeting

  if (!matchesBannerPage(banner, dashboardData, pathname)) {
    return false;
  }

  // User role / plan / region targeting

  if (!matchesBannerTarget(banner, viewer)) {
    return false;
  }

  // Start and end scheduling

  if (!isBannerScheduled(banner, now)) {
    return false;
  }

  // Previously dismissed revision

  const dismissed = dismissals[banner.bannerId];

  const isDismissed =
    dismissed?.viewed === 1 &&
    dismissed.revision ===
      getBannerRevision(banner, dashboardData?.updatedAt);

  if (isDismissed) {
    return false;
  }

  return true;
};

// -------------------------------------------------------
// Measure and observe the existing Header position
// -------------------------------------------------------

const useHeaderOverlayPosition = (
  pathname: string,
  anchorSelector: string | undefined,
  sidebarOffset: number
) => {
  const markerRef = useRef<HTMLSpanElement | null>(null);
  const [position, setPosition] = useState<OverlayPosition | null>(null);

  // -----------------------------------------------------
  // 3. Measure the existing Header position
  // -----------------------------------------------------

  const updatePosition = useCallback(() => {
    const marker = markerRef.current;

    if (!marker?.parentElement) {
      return;
    }

    const headerRoot = marker.parentElement;

    const configuredAnchor = anchorSelector
      ? document.querySelector<HTMLElement>(
          anchorSelector
        )
      : null;

    // The preceding element should be the final row
    // of the PepWise Header, normally TabPageHeader.
    //
    // anchorSelector can override this behavior for
    // a header that has a different DOM structure.

    const anchorElement =
      configuredAnchor ||
      (
        marker.previousElementSibling as
          HTMLElement | null
      );

    const headerRect =
      headerRoot.getBoundingClientRect();

    const anchorRect = anchorElement
      ? anchorElement.getBoundingClientRect()
      : headerRect;

    const viewportWidth = window.innerWidth;

    // Apply sidebar offset only when the Header
    // is not already positioned after the sidebar.

    const requestedOffset = Math.max(
      0,
      sidebarOffset
    );

    const left = Math.min(
      viewportWidth,
      Math.max(
        0,
        headerRect.left,
        requestedOffset
      )
    );

    const right = Math.min(
      viewportWidth,
      headerRect.right
    );

    const nextPosition: OverlayPosition = {
      top: Math.max(
        0,
        Math.round(anchorRect.bottom)
      ),

      left: Math.round(left),

      width: Math.max(
        0,
        Math.round(right - left)
      ),
    };

    setPosition((previous) => {
      if (
        previous?.top === nextPosition?.top &&
        previous?.left === nextPosition?.left &&
        previous?.width === nextPosition?.width
      ) {
        return previous;
      }

      return nextPosition;
    });
  }, [
    anchorSelector,
    sidebarOffset,
  ]);

  // -----------------------------------------------------
  // 4. Track Header resize, route and window changes
  // -----------------------------------------------------

  useLayoutEffect(() => {
    const marker = markerRef.current;

    if (!marker?.parentElement) {
      return;
    }

    const headerRoot = marker.parentElement;

    let frameId: number | null = null;

    const schedulePositionUpdate = () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        updatePosition();
      });
    };

    updatePosition();

    // Track header height changes.
    // For example, when tabs appear or disappear.

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(schedulePositionUpdate)
        : null;

    resizeObserver?.observe(headerRoot);

    const configuredAnchor = anchorSelector
      ? document.querySelector<HTMLElement>(
          anchorSelector
        )
      : null;

    const anchorElement =
      configuredAnchor ||
      (
        marker.previousElementSibling as
          HTMLElement | null
      );

    if (
      anchorElement &&
      anchorElement !== headerRoot
    ) {
      resizeObserver?.observe(anchorElement);
    }

    // Watch changes in the header structure,
    // including conditional rendering of tabs.

    const mutationObserver =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver(
            schedulePositionUpdate
          )
        : null;

    mutationObserver?.observe(headerRoot, {
      childList: true,
      subtree: false,
    });

    window.addEventListener(
      'resize',
      schedulePositionUpdate
    );

    window.addEventListener(
      'scroll',
      schedulePositionUpdate,
      true
    );

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      resizeObserver?.disconnect();
      mutationObserver?.disconnect();

      window.removeEventListener(
        'resize',
        schedulePositionUpdate
      );

      window.removeEventListener(
        'scroll',
        schedulePositionUpdate,
        true
      );
    };
  }, [
    pathname,
    anchorSelector,
    updatePosition,
  ]);

  return { markerRef, position };
};

interface BannerExpandButtonProps {
  expanded: boolean;
  additionalCount: number;
  onToggle: () => void;
  textColor: string;
}

const BannerExpandButton: React.FC<BannerExpandButtonProps> = ({
  expanded,
  additionalCount,
  onToggle,
  textColor,
}) => (
  <Button
    type="button"
    size="small"
    aria-label={
      expanded
        ? 'Collapse announcements'
        : `Expand ${additionalCount} additional announcements`
    }
    aria-expanded={expanded}
    onClick={onToggle}
    endIcon={
      expanded ? (
        <ExpandLessIcon sx={{ fontSize: 16 }} />
      ) : (
        <ExpandMoreIcon sx={{ fontSize: 16 }} />
      )
    }
    sx={{
      minWidth: 0,
      minHeight: 28,
      px: 0.75,
      fontSize: 11,
      fontWeight: 600,
      textTransform: 'none',
      whiteSpace: 'nowrap',
      color: `${textColor} !important`,
      '& .MuiButton-endIcon': { ml: 0.25 },
      '& .MuiSvgIcon-root': {
        color: `${textColor} !important`,
      },
      '&:hover, &:active': {
        color: `${textColor} !important`,
      },
      '&:focus-visible, &.Mui-focusVisible': {
        color: `${textColor} !important`,
      },
    }}
  >
    {expanded ? 'Show less' : `+${additionalCount} more`}
  </Button>
);

interface BannerStackProps {
  primaryBanner: DynamicBanner;
  visibleBanners: DynamicBanner[];
  position: OverlayPosition;
  bannerStyleConfig: BannerStyleConfig;
  onDismiss: (banner: DynamicBanner) => void;
}

// -------------------------------------------------------
// Render and interact with the banner stack
// -------------------------------------------------------

const BannerStack: React.FC<BannerStackProps> = ({
  primaryBanner,
  visibleBanners,
  position,
  bannerStyleConfig,
  onDismiss,
}) => {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const hoveringRef = useRef(false);
  const [expanded, setExpanded] = useState(false);
  const dismissBanner = onDismiss;
  const BANNER_STYLE_CONFIG = bannerStyleConfig;

  // -----------------------------------------------------
  // 5. Reset expanded state when banners disappear
  // -----------------------------------------------------

  useEffect(() => {
    if (visibleBanners.length <= 1) {
      setExpanded(false);
    }
  }, [visibleBanners.length]);

  // -----------------------------------------------------
  // 6. Handle hover expansion
  // -----------------------------------------------------

  const handleMouseEnter = () => {
    hoveringRef.current = true;

    if (visibleBanners.length > 1) {
      setExpanded(true);
    }
  };

  const handleMouseLeave = () => {
    hoveringRef.current = false;

    const element = overlayRef.current;

    if (
      !element?.contains(document.activeElement)
    ) {
      setExpanded(false);
    }
  };

  // -----------------------------------------------------
  // 7. Keyboard focus behavior
  // -----------------------------------------------------

  const handleBlurCapture = (
    event: React.FocusEvent<HTMLDivElement>
  ) => {
    const nextFocusedElement = event.relatedTarget;

    if (
      !hoveringRef.current &&
      !event.currentTarget.contains(
        nextFocusedElement as Node | null
      )
    ) {
      setExpanded(false);
    }
  };

  // -----------------------------------------------------
  // 8. Nothing to display
  // -----------------------------------------------------

  const additionalBanners = visibleBanners.slice(1);

  const additionalCount = additionalBanners.length;

  const previewBanners = additionalBanners.slice(
    0,
    3
  );

  // -----------------------------------------------------
  // 9. Render banner content
  // -----------------------------------------------------

  return (
      <Box
        sx={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          width: position.width,
          zIndex: (theme) => theme.zIndex.drawer - 1,
          pointerEvents: 'none',
          boxSizing: 'border-box',
        }}
      >
        <Box
          ref={overlayRef}
          role="region"
          aria-label="PepWise announcements"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onBlurCapture={handleBlurCapture}
          sx={{
            width: '100%',
            boxSizing: 'border-box',
            pointerEvents: 'auto',
            overflow: 'hidden',
            borderRadius: '0 0 8px 8px',
            boxShadow:
              '0 8px 20px rgba(0, 0, 0, 0.14)',
          }}
        >
          {/* Scrollable banner group */}

          <Box
            sx={{
              maxHeight: expanded
                ? `max(80px, min(65vh, calc(100vh - ${
                    position.top + 12
                  }px)))`
                : 'none',

              overflowY: expanded
                ? 'auto'
                : 'visible',

              overflowX: 'hidden',
              scrollbarWidth: 'thin',
            }}
          >
            {/* Main banner */}

            <BannerItem
              banner={primaryBanner}
              bannerStyleConfig={BANNER_STYLE_CONFIG}
              onDismiss={dismissBanner}
              extraAction={
                additionalCount > 0 ? (
                  <BannerExpandButton
                    expanded={expanded}
                    additionalCount={additionalCount}
                    onToggle={() => setExpanded((previous) => !previous)}
                    textColor={getBannerColors(primaryBanner, BANNER_STYLE_CONFIG).text}
                  />
                ) : null
              }
            />

            {/* Expanded banners */}

            {expanded &&
              additionalBanners.map(
                (banner) => (
                  <BannerItem
                    key={banner.bannerId}
                    banner={banner}
                    bannerStyleConfig={BANNER_STYLE_CONFIG}
                    onDismiss={dismissBanner}
                    showDivider
                  />
                )
              )}
          </Box>

          {/* Collapsed colored preview strips */}

          {!expanded &&
            previewBanners.map(
              (banner, index) => {
                const colors =
                  getBannerColors(banner, BANNER_STYLE_CONFIG);

                return (
                  <Box
                    key={banner.bannerId}
                    aria-hidden="true"
                    sx={{
                      height: 5,
                      width: '100%',
                      mx: 'auto',
                      backgroundColor:
                        colors.accent,
                      borderRadius:
                        index ===
                        previewBanners.length - 1
                          ? '0 0 4px 4px'
                          : 0,
                    }}
                  />
                );
              }
            )}
        </Box>
      </Box>
  );
};

// -------------------------------------------------------
// Dynamic banner overlay
// -------------------------------------------------------

export const DynamicBannerOverlay: React.FC<
  DynamicBannerOverlayProps
> = ({
  viewer,
  placement = 'below-tabs',
  sidebarOffset = 60,
  anchorSelector,
}) => {
  const { pathname } = useLocation();

  const theme = useTheme();
  const colors = theme.palette;

  const BANNER_STYLE_CONFIG: BannerStyleConfig = {
    info: {
      background: colors.bannerColors?.info?.background ?? colors.background.paper,
      text: colors.bannerColors?.info?.text ?? colors.text.primary,
      accent: colors.bannerColors?.info?.accent ?? colors.divider,
      fontSize: colors.bannerColors?.info?.fontSize ?? 13,
      buttonBackground: colors.bannerColors?.info?.buttonBackground ?? colors.background.paper,
      buttonHoverBackground: colors.bannerColors?.info?.buttonHoverBackground ?? colors.action.hover,
      buttonBorder: colors.bannerColors?.info?.buttonBorder ?? colors.primary.main,
    },
    success: {
      background: colors.bannerColors?.success?.background ?? colors.background.paper,
      text: colors.bannerColors?.success?.text ?? colors.text.primary,
      accent: colors.bannerColors?.success?.accent ?? colors.divider,
      fontSize: colors.bannerColors?.success?.fontSize ?? 13,
      buttonBackground: colors.bannerColors?.success?.buttonBackground ?? colors.background.paper,
      buttonHoverBackground: colors.bannerColors?.success?.buttonHoverBackground ?? colors.action.hover,
      buttonBorder: colors.bannerColors?.success?.buttonBorder ?? colors.primary.main,
    },
    warning: {
      background: colors.bannerColors?.warning?.background ?? colors.background.paper,
      text: colors.bannerColors?.warning?.text ?? colors.text.primary,
      accent: colors.bannerColors?.warning?.accent ?? colors.divider,
      fontSize: colors.bannerColors?.warning?.fontSize ?? 13,
      buttonBackground: colors.bannerColors?.warning?.buttonBackground ?? colors.background.paper,
      buttonHoverBackground: colors.bannerColors?.warning?.buttonHoverBackground ?? colors.action.hover,
      buttonBorder: colors.bannerColors?.warning?.buttonBorder ?? colors.primary.main,
    },
    critical: {
      background: colors.bannerColors?.critical?.background ?? colors.background.paper,
      text: colors.bannerColors?.critical?.text ?? colors.text.primary,
      accent: colors.bannerColors?.critical?.accent ?? colors.divider,
      fontSize: colors.bannerColors?.critical?.fontSize ?? 13,
      buttonBackground: colors.bannerColors?.critical?.buttonBackground ?? colors.background.paper,
      buttonHoverBackground: colors.bannerColors?.critical?.buttonHoverBackground ?? colors.action.hover,
      buttonBorder: colors.bannerColors?.critical?.buttonBorder ?? colors.primary.main,
    },
  };


  // The marker is placed after the Header/Tab row.
  // Only the portal creates the visible banner UI.

  const { markerRef, position } = useHeaderOverlayPosition(
    pathname,
    anchorSelector,
    sidebarOffset
  );

  // -----------------------------------------------------
  // 1. Load banner configurations
  // -----------------------------------------------------

  const {
    banners,
    dashboardData,
    dismissals,
    dismissalsReady,
    now,
    dismissBanner,
  } = useDynamicBanners(viewer, pathname);

  // -----------------------------------------------------
  // 2. Filter banners
  // -----------------------------------------------------

  const visibleBanners = useMemo(() => {
    if (!viewer || !dismissalsReady) {
      return [];
    }

    const context: BannerEligibilityContext = {
      viewer,
      dashboardData,
      dismissals,
      now,
      pathname,
      placement,
    };

    const eligibleBanners = banners.filter(
      (banner) => isBannerEligible(banner, context)
    );

    return sortBannersByPriority(eligibleBanners);
  }, [
    banners,
    dashboardData,
    dismissals,
    dismissalsReady,
    now,
    pathname,
    placement,
    viewer,
  ]);

  // -----------------------------------------------------
  // 3. Render the eligible banners
  // -----------------------------------------------------

  const primaryBanner = visibleBanners[0];

  const bannerContent =
    viewer && primaryBanner && position && position.width > 0 ? (
      <BannerStack
        primaryBanner={primaryBanner}
        visibleBanners={visibleBanners}
        position={position}
        bannerStyleConfig={BANNER_STYLE_CONFIG}
        onDismiss={dismissBanner}
      />
    ) : null;

  // -----------------------------------------------------
  // 10. Portal rendering
  // -----------------------------------------------------

  return (
    <>
      {/* 
        Invisible insertion marker.

        Place DynamicBannerOverlay immediately after
        the Header's final tab/header row.

        The marker does not add height or spacing.
      */}

      <span
        ref={markerRef}
        style={{
          display: 'none',
        }}
      />

      {typeof document !== 'undefined' &&
        bannerContent &&
        createPortal(
          bannerContent,
          document.body
        )}
    </>
  );
};

export default DynamicBannerOverlay;
